import React, {useState} from 'react';
import SiteIcon from './SiteIcon';
import ChesshoodProject from './ChesshoodProject';
import './PortfolioProjects.css';

const mathsTranslations = {
  "en": {
    "category": "Mathematics learning",
    "status": "Learning app · PL / EN / ES",
    "purpose": "Three learning stages: primary school, high school and university foundations. Explore 84 topics and 252 real-life examples, from addition through functions and trigonometry to integrals and matrices. Practise with hints, earn points and discover mathematical history with sources, in Polish, English or Spanish.",
    "build": "HTML, CSS and modular JavaScript with exercises at three levels, answer checking, adaptive review, hints, points and progress tracking.",
    "note": "No account is needed. Progress is saved in your browser; export or import a backup to move it between devices.",
    "technologies": [
      "JavaScript",
      "Adaptive practice",
      "PL / EN / ES"
    ],
    "action": "Learn maths",
    "labels": {
      "open": "Open",
      "build": "How it’s built",
      "technologies": "technologies",
      "summaryLanguage": "Summary language"
    },
    "previewCopy": {
      "label": "UNDERSTAND. PRACTISE. DISCOVER.",
      "first": "Small steps.",
      "second": "Big discoveries.",
      "footer": "3 STAGES / 84 TOPICS / 3 LANGUAGES"
    }
  },
  "pl": {
    "category": "Nauka matematyki",
    "status": "Aplikacja do nauki · PL / EN / ES",
    "purpose": "Trzy etapy nauki: szkoła podstawowa, liceum i fundamenty uniwersyteckie. Poznaj 84 tematy i 252 przykłady zastosowań w życiu — od dodawania przez funkcje i trygonometrię po całki i macierze. Ćwicz z podpowiedziami, zdobywaj punkty i odkrywaj historie matematyki ze źródłami, po polsku, angielsku lub hiszpańsku.",
    "build": "HTML, CSS i modułowy JavaScript: zadania na trzech poziomach, sprawdzanie odpowiedzi, powtórki dopasowane do postępów, podpowiedzi, punkty i zapis wyników.",
    "note": "Nie potrzebujesz konta. Postępy zapisują się w przeglądarce; eksport i import kopii pozwala przenieść je na inne urządzenie.",
    "technologies": [
      "JavaScript",
      "Ćwiczenia adaptacyjne",
      "PL / EN / ES"
    ],
    "action": "Ucz się matematyki",
    "labels": {
      "open": "Otwórz",
      "build": "Jak powstała aplikacja",
      "technologies": "technologie",
      "summaryLanguage": "Język opisu"
    },
    "previewCopy": {
      "label": "ZROZUM. PRZEĆWICZ. ODKRYJ.",
      "first": "Małe kroki.",
      "second": "Wielkie odkrycia.",
      "footer": "3 ETAPY / 84 TEMATY / 3 JĘZYKI"
    }
  },
  "es": {
    "category": "Aprendizaje de matemáticas",
    "status": "Aplicación educativa · PL / EN / ES",
    "purpose": "Tres etapas: primaria, secundaria y bachillerato, y fundamentos universitarios. Explora 84 temas y 252 ejemplos reales, desde la suma y las funciones hasta la trigonometría, las integrales y las matrices. Practica con pistas, gana puntos y descubre historias con fuentes, en polaco, inglés o español.",
    "build": "HTML, CSS y JavaScript modular con ejercicios de tres niveles, comprobación de respuestas, repaso adaptado al progreso, pistas, puntos y seguimiento de resultados.",
    "note": "No necesitas una cuenta. El progreso se guarda en el navegador; exporta o importa una copia para trasladarlo a otro dispositivo.",
    "technologies": [
      "JavaScript",
      "Práctica adaptativa",
      "PL / EN / ES"
    ],
    "action": "Aprende matemáticas",
    "labels": {
      "open": "Abrir",
      "build": "Cómo está hecha",
      "technologies": "tecnologías",
      "summaryLanguage": "Idioma del resumen"
    },
    "previewCopy": {
      "label": "COMPRENDE. PRACTICA. DESCUBRE.",
      "first": "Pasos pequeños.",
      "second": "Grandes hallazgos.",
      "footer": "3 ETAPAS / 84 TEMAS / 3 IDIOMAS"
    }
  }
};

const latestProjects = [
  {
    id: 'maths',
    name: 'Taldigi Maths',
    href: '/maths',
    preview: 'maths',
    ...mathsTranslations.en,
    translations: mathsTranslations,
  },
  {
    id: 'language',
    name: 'LinguiChess',
    category: 'Language learning',
    status: 'Web app · PL / EN / ES',
    href: '/language',
    preview: 'language',
    purpose: 'Practise Polish, English and Spanish vocabulary in six directions. Learn new words, type your answers, earn points and spend more time on the words you find difficult.',
    build: 'HTML, CSS and JavaScript, with an adaptive review system, a personal dictionary, pronunciation and optional translation lookup.',
    note: 'Progress is saved in your browser. Export a backup to keep or move your vocabulary.',
    technologies: ['JavaScript', 'Browser storage', 'Speech synthesis'],
    action: 'Try the vocabulary app',
  },
  {
    id: 'spanish-native',
    name: 'Spanish Native UK',
    category: 'Teaching & lesson management',
    status: 'Web app · Sign-in for classes',
    href: '/spanishnativeuk',
    image: '/images/spanishnative/hero.webp',
    alt: 'AI-generated Spanish courtyard used on the Spanish Native UK website',
    purpose: 'A home for Spanish lessons, with a class calendar, student reservations and a tutor workspace for lesson links, learning materials and homework.',
    build: 'A responsive React app connected to Python Azure Functions and MongoDB through a Node API proxy, with email-code sign-in and calendar exports.',
    note: 'The tutor sets availability and class links. Student and tutor areas require sign-in.',
    technologies: ['React', 'Python / Azure', 'MongoDB'],
    action: 'Explore Spanish Native UK',
  },
  {
    id: 'ai-course',
    name: 'AI for beginners',
    category: 'Practical AI education',
    status: 'Course website · EN / PL / ES',
    href: '/aicours',
    preview: 'ai',
    purpose: 'Discover a six-module introduction to using AI for everyday tasks and work. Explore practical prompt examples and enquire about the course in English, Polish or Spanish.',
    build: 'React with multilingual course content, interactive examples, one-click prompt copying and a validated enquiry form connected to the Azure email service.',
    note: 'The current site presents the programme and examples, with a form to enquire about joining.',
    technologies: ['React', 'Multilingual content', 'Azure email'],
    action: 'Explore the AI course',
  },
  {
    id: 'chess-hampshire',
    name: 'Chess Hampshire',
    category: 'County chess & community',
    status: 'Community website',
    href: '/chesshampshire',
    image: '/images/chesshampshire/hero.webp',
    alt: 'Wooden chess pieces and a cup of tea from the Chess Hampshire website',
    purpose: 'Bring Hampshire chess together: news, a searchable article archive, local clubs, junior events, tournaments, county teams and chess-clock sale enquiries.',
    build: 'A custom React front end on Azure, using structured content and year-by-year article loading. The guest chess Beta adds chess.js and a server-backed game lobby.',
    note: 'The online chess room is a Beta for friendly guest games.',
    technologies: ['React', 'Azure', 'Structured content'],
    action: 'Visit Chess Hampshire',
    links: [
      {label: 'Play online · Beta', href: '/chesshampshire/play-test', rel: 'nofollow'},
      {label: 'HCA clock shop', href: '/chesshampshire/news/hampshire-tournament-chess-clocks-for-sale'},
    ],
  },
  {
    id: 'organisation',
    name: 'Organisation planner',
    category: 'Planning & productivity',
    status: 'Private app · Sign-in required',
    href: '/organizacja',
    rel: 'nofollow',
    preview: 'planner',
    purpose: 'A private weekly workspace for organising topics, tasks, notes and progress, with an AI assistant and English, Polish and Spanish interfaces.',
    build: 'React, Python Azure Functions and MongoDB, with email-code sign-in, a Node API proxy and a server-side Azure OpenAI integration.',
    note: 'Access is restricted to the owner. This link opens the sign-in page.',
    technologies: ['React', 'MongoDB', 'Azure OpenAI'],
    action: 'Open planner sign-in',
  },
  {
    id: 'woodshare',
    name: 'Woodshare',
    category: 'Material reuse',
    status: 'Interactive concept demo',
    href: '/woodshare',
    image: '/images/woodshare/workshop.webp',
    alt: 'AI-generated Woodshare workshop with reusable timber and plywood offcuts',
    purpose: 'Explore a way to give spare timber and sheet materials another use. Search and filter sample materials, add a demo listing with photos and try the reservation flow.',
    build: 'HTML, CSS and JavaScript with Polish and English interfaces, browser storage and local photo processing.',
    note: 'A browser-local demo with sample listings. Listings and reservations are not shared with other users.',
    technologies: ['JavaScript', 'Browser storage', 'Photo uploads'],
    action: 'Try the Woodshare demo',
  },
];

const businessProjects = [
  {
    id: 'bodyshop',
    name: 'Bodyshop Hood',
    category: 'Automotive',
    status: 'Business website',
    href: '/bodyshop',
    image: '/images/bodyshop/friendly-polishing-960.webp',
    alt: 'Bodyshop Hood website showing a technician polishing a car',
    purpose: 'Introduce body repair and paint services, show completed work and let customers send repair enquiries with photographs of their vehicle.',
    build: 'React with a responsive project gallery, photo uploads and Azure-backed enquiry handling.',
    technologies: ['React', 'Photo uploads', 'Azure'],
    action: 'Visit Bodyshop Hood',
    links: [{label: 'View completed repairs', href: '/bodyshop/our-work'}],
  },
  {
    id: 'atelier',
    name: 'Atelier Hair',
    category: 'Hair & beauty',
    status: 'Fictional website demo',
    href: '/hairdresser',
    image: '/images/salon/hero.webp',
    alt: 'AI-generated hair salon scene from the Atelier Hair demonstration',
    purpose: 'Show a salon’s services, example prices and hairstyles, then help visitors find the location and send a service enquiry.',
    build: 'React with a dedicated visual identity, responsive gallery, image lightbox, optional map and validated Azure email enquiries.',
    note: 'A fictional salon with illustrative photography. Enquiries go to Taldigi and do not book appointments.',
    technologies: ['React', 'Image gallery', 'Azure email'],
    action: 'Explore Atelier Hair',
  },
  {
    id: 'willow-house',
    name: 'The Willow House',
    category: 'Boutique hotel',
    status: 'Fictional website demo',
    href: '/hotel',
    image: '/images/stays/hotel-exterior-refined.webp',
    alt: 'AI-generated red brick hotel exterior for the Willow House demonstration',
    purpose: 'Introduce a boutique hotel, present its rooms and surroundings, and collect stay enquiries with dates and guest numbers.',
    build: 'React with a bespoke hotel design, photo gallery, location map and an enquiry form that checks dates and room capacity.',
    note: 'A fictional hotel with illustrative photography. The form sends an enquiry, not a confirmed booking.',
    technologies: ['React', 'Date validation', 'Azure email'],
    action: 'Explore The Willow House',
  },
  {
    id: 'field-shore',
    name: 'Field & Shore',
    category: 'Holiday homes',
    status: 'Fictional website demo',
    href: '/holiday-homes',
    image: '/images/stays/stay-cottage-refined.webp',
    alt: 'AI-generated holiday cottage from the Field and Shore demonstration',
    purpose: 'Browse a collection of holiday homes, compare property details and photographs, and enquire about a stay.',
    build: 'React with responsive property cards, galleries, a map and validated accommodation enquiries for dates and guest capacity.',
    note: 'A fictional collection with sample properties. No accommodation booking or payment is taken.',
    technologies: ['React', 'Property catalogue', 'Azure email'],
    action: 'Explore Field & Shore',
  },
  {
    id: 'mila',
    name: 'MILA Beauty Studio',
    category: 'Nails & skincare',
    status: 'Fictional website demo',
    href: '/beauty-studio',
    image: '/images/beauty/mila-hero.webp',
    alt: 'Illustrative cherry-red manicure and skincare campaign for the MILA Beauty Studio demo',
    purpose: 'Present nail and skincare treatments through a beauty studio website with a gallery, a short visual film and service enquiries.',
    build: 'React with a custom ivory and cherry-red design, a video montage, an optional map and a validated Azure email enquiry form.',
    note: 'A fictional studio with illustrative photography. Enquiries do not book an appointment.',
    technologies: ['React', 'Video & gallery', 'Azure email'],
    action: 'Explore MILA Beauty Studio',
  },
];

function ProjectPreview({project}) {
  if (project.image) return <img src={project.image} alt={project.alt} loading="lazy" width="720" height="480"/>;
  if (project.preview === 'maths') return <div className="catalogue-art catalogue-art-maths" aria-hidden="true">
    <img className="catalogue-maths-photo" src="/maths/images/maths-studio.webp" alt="" loading="lazy" decoding="async" width="1536" height="1024"/>
    <div className="catalogue-maths-story">
      <span className="catalogue-art-label">{project.previewCopy.label}</span>
      <strong>{project.previewCopy.first}<span>{project.previewCopy.second}</span></strong>
    </div>
    <span className="catalogue-art-footer">{project.previewCopy.footer}</span>
  </div>;
  if (project.preview === 'language') return <div className="catalogue-art catalogue-art-language" aria-hidden="true">
    <span className="catalogue-art-label">A LITTLE PRACTICE. WORDS THAT STAY.</span>
    <strong>hello<span>hola</span><em>cześć</em></strong>
    <span className="catalogue-art-footer">POLSKI / ENGLISH / ESPAÑOL</span>
  </div>;
  if (project.preview === 'ai') return <div className="catalogue-art catalogue-art-ai" aria-hidden="true">
    <span className="catalogue-art-label">AI FOR EVERYDAY LIFE</span>
    <SiteIcon name="agent"/>
    <strong>Start curious.<br/><em>Build confidence.</em></strong>
    <span className="catalogue-art-footer">6 MODULES / 3 LANGUAGES</span>
  </div>;
  return <div className="catalogue-art catalogue-art-planner" aria-hidden="true">
    <span className="catalogue-art-label">YOUR PRIVATE WORKSPACE</span>
    <strong>Plan.<br/><span>Do.</span><br/><em>Review.</em></strong>
    <span className="catalogue-art-footer">TASKS / NOTES / PROGRESS</span>
  </div>;
}

function ProjectCard({project: originalProject, number}) {
  const [summaryLanguage, setSummaryLanguage] = useState('en');
  const project = {...originalProject, ...(originalProject.translations?.[summaryLanguage] || {})};
  const labels = project.labels || {open: 'Open', build: 'How it’s built', technologies: 'technologies'};
  return <article className="catalogue-card" lang={originalProject.translations ? summaryLanguage : undefined} id={`project-${project.id}`} aria-labelledby={`project-${project.id}-title`}>
    <a className="catalogue-visual" href={project.href} rel={project.rel} aria-label={`${labels.open} ${project.name}`}>
      <ProjectPreview project={project}/>
      <span className="catalogue-status">{project.status}</span>
      <span className="catalogue-arrow" aria-hidden="true"><SiteIcon/></span>
    </a>
    <div className="catalogue-copy">
      <p className="catalogue-category">{String(number).padStart(2, '0')} / {project.category}</p>
      <h3 id={`project-${project.id}-title`}><a href={project.href} rel={project.rel}>{project.name}</a></h3>
      {originalProject.translations && <label className="catalogue-summary-language">
        <span>{labels.summaryLanguage}</span>
        <select value={summaryLanguage} onChange={event=>setSummaryLanguage(event.target.value)} aria-label={`${project.name}: ${labels.summaryLanguage}`}>
          <option value="en" lang="en">EN · English</option>
          <option value="pl" lang="pl">PL · Polski</option>
          <option value="es" lang="es">ES · Español</option>
        </select>
      </label>}
      <p className="catalogue-purpose">{project.purpose}</p>
      <div className="catalogue-build"><h4>{labels.build}</h4><p>{project.build}</p></div>
      <ul className="catalogue-technologies" aria-label={`${project.name}: ${labels.technologies}`}>{project.technologies.map(technology=><li key={technology}>{technology}</li>)}</ul>
      {project.note && <p className="catalogue-note">{project.note}</p>}
      <div className="catalogue-actions"><a className="catalogue-primary-link" href={project.href} rel={project.rel}>{project.action}<SiteIcon/></a>
        {project.links && <div className="catalogue-related-links">{project.links.map(link=><a key={link.href} href={link.href} rel={link.rel}>{link.label}<SiteIcon/></a>)}</div>}
      </div>
      <a className="catalogue-url" href={project.href} rel={project.rel}>taldigi.info{project.href}</a>
    </div>
  </article>;
}

export default function PortfolioProjects() {
  return <section className="wrap work-section project-catalogue" id="work" aria-labelledby="work-title">
    <div className="section-heading"><div><p className="eyebrow">WEBSITES, APPS & WORKING IDEAS</p><h2 id="work-title">Built to be used.<br/><em>Ready to explore.</em></h2></div>
      <p>Explore {latestProjects.length + businessProjects.length + 1} projects, from business websites to learning apps and private tools. See what each one does, how it is built and open it for yourself.</p>
    </div>
    <nav className="catalogue-jump-links" aria-label="Portfolio sections">
      <a href="#latest-projects">Latest apps & projects <span>{latestProjects.length}</span></a>
      <a href="#business-projects">Business websites <span>{businessProjects.length}</span></a>
      <a href="#chesshood">Chesshood · Web & mobile <SiteIcon/></a>
    </nav>
    <div className="catalogue-group-heading" id="latest-projects"><h3>Latest apps & projects</h3><p>Learning, community and everyday tools.</p></div>
    <div className="catalogue-list">{latestProjects.map((project,index)=><ProjectCard project={project} number={index+1} key={project.id}/>)}</div>
    <div className="catalogue-group-heading" id="business-projects"><h3>Business websites</h3><p>Individual design for different kinds of business.</p></div>
    <div className="catalogue-list">{businessProjects.map((project,index)=><ProjectCard project={project} number={latestProjects.length+index+1} key={project.id}/>)}</div>
    <ChesshoodProject/>
  </section>;
}

